import { useEffect, useState } from 'react';
import { withType } from './GovernanceShared';

// ─────────────────────────────────────────────────────────────────────────────
// useGovernanceLifecycle — the version-lifecycle orchestration shared verbatim by
// BOTH editors (DataControlsEditor for rules, AppControlsEditor for apps). It owns
// the generic concerns — mount load, ready/reseed gating, the Save Draft → Submit
// for Approval → Publish flow, Reject, Cancel, in-place refresh, and the swallowed-
// error handling (the `api` middleware resolves `undefined` on failure, so every
// mutation checks its result before advancing). What differs per editor is passed
// in: the staged-collection `flush()`, its `dirty` flag, `allowInitialSave`, the
// active draft's `policyVersionId`, and the noun used in the save-error copy.
//
// The editor keeps its own staging + draft-meta derivation (see draftMetaOf in
// GovernanceShared); this hook only needs the resolved `policyVersionId`. That
// keeps `flush`/`ensureDraft` (which need the draft meta) free of any dependency
// on this hook's return value.
// ─────────────────────────────────────────────────────────────────────────────
export default function useGovernanceLifecycle({
  customerId,
  controlType = 'DATA',
  history,
  policy, publishing, policyVersionId,
  // editor-specific staging
  flush, dirty, allowInitialSave = false, saveErrorNoun = 'changes',
  // dispatch-bound thunks (DATA bindings simply ignore the trailing controlType)
  getGovernanceDraft, getGovernancePolicy,
  approveGovernancePolicy, rejectGovernancePolicy, publishGovernancePolicy,
}) {
  const [ready, setReady] = useState(false);
  const [reseedKey, setReseedKey] = useState(0);
  const [popup, setPopup] = useState(null); // 'cancel' | 'approve' | 'publish' | 'reject'
  const [rejectReason, setRejectReason] = useState('');
  const [errorPopup, setErrorPopup] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  // Load draft + policy for this control type on mount.
  useEffect(() => {
    if (!customerId) { setReady(true); return; }
    Promise.all([
      getGovernanceDraft(customerId, controlType),
      getGovernancePolicy(customerId, controlType),
    ]).then(() => setReady(true));
  }, [customerId, controlType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── navigation ──
  const backToLanding = () => {
    if (customerId) history.push(withType(`/customers/${customerId}/governance`, controlType));
    else history.push('/');
  };

  // Re-fetch draft + policy and bump reseedKey so the editor re-seeds its staged
  // rows. Used after Save Draft / Submit for Approval / Reject (banner advances in
  // place); only Cancel and a successful Publish leave the editor.
  const refresh = async () => {
    if (!customerId) return;
    await Promise.all([
      getGovernanceDraft(customerId, controlType),
      getGovernancePolicy(customerId, controlType),
    ]);
    setReseedKey((k) => k + 1);
  };

  // ── lifecycle actions ──
  const handleSaveDraft = async () => {
    if (!customerId || (!dirty && !allowInitialSave)) return;
    setSavingDraft(true);
    try {
      const vid = await flush();
      if (vid == null) {
        setErrorPopup(`Some changes could not be saved. Please review the ${saveErrorNoun} and try again.`);
        return;
      }
      await refresh();
    } finally { setSavingDraft(false); }
  };

  const handleCancelClick = () => {
    if (dirty) setPopup('cancel');
    else backToLanding();
  };
  const handleCancelConfirmed = () => { setPopup(null); backToLanding(); };

  const handleApproveConfirmed = async () => {
    setPopup(null);
    if (!customerId) return;
    setSavingDraft(true);
    try {
      const vid = await flush();
      if (vid == null) {
        setErrorPopup('The draft could not be saved, so it was not submitted for approval. Please try again.');
        return;
      }
      const res = await approveGovernancePolicy(customerId, vid, controlType);
      if (res === undefined) {
        setErrorPopup('Failed to submit the draft for approval. Please try again.');
        return;
      }
      await refresh();
    } finally { setSavingDraft(false); }
  };

  const handleRejectConfirmed = async () => {
    setPopup(null);
    if (!customerId || !policyVersionId) return;
    setSavingDraft(true);
    try {
      const res = await rejectGovernancePolicy(customerId, policyVersionId, rejectReason, controlType);
      if (res === undefined) {
        setErrorPopup('Failed to reject the draft. Please try again.');
        return;
      }
      setRejectReason('');
      await refresh();
    } finally { setSavingDraft(false); }
  };

  const handlePublishConfirmed = async () => {
    setPopup(null);
    if (!customerId || !policyVersionId) return;
    const res = await publishGovernancePolicy(customerId, policyVersionId, controlType);
    if (res === undefined) {
      setErrorPopup('Failed to publish the policy. It has not gone live. Please try again.');
      return;
    }
    backToLanding();
  };

  const publishedText = () => {
    if (policy && policy.version) {
      const vNum = policy.version.VersionNumber ?? policy.version.versionNumber;
      return `Currently published: ${vNum ? `v${vNum}` : 'live version'}`;
    }
    return 'No policy published yet';
  };

  const isBusy = savingDraft || publishing;

  return {
    // gating / seeding signals for the editor
    ready, reseedKey,
    // status flags
    isBusy, savingDraft, publishing,
    // popup + reject state (wired into GovernanceLifecycleModals)
    popup, setPopup, rejectReason, setRejectReason, errorPopup, setErrorPopup,
    // handlers
    backToLanding, publishedText,
    handleSaveDraft, handleCancelClick, handleCancelConfirmed,
    handleApproveConfirmed, handleRejectConfirmed, handlePublishConfirmed,
  };
}
